import { motion } from 'framer-motion';
import hexToRgba from 'hex-to-rgba';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useCallback, useState } from 'react';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import { TERMS, POLICY } from '@/assets/md/terms';
import { FlexBox } from '@/components/atom/FlexBox';
import { dashboardContainerVariant } from '@/styles/motion';

const Container = styled(FlexBox)`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const TermsWrapper = styled(Markdown)`
  a {
    text-decoration: underline;
  }

  hr {
    margin: 28px 0;
    ${(p) => p.theme.border.gray}
    border-top: 0;
  }

  h2 {
    font-size: ${(p) => p.theme.size.md};
    ${(p) => p.theme.font.bold};
    line-height: 32px;
  }

  p {
    font-size: ${(p) => p.theme.size.sm};
    ${(p) => p.theme.font.regular};
  }

  h2 + p {
    margin-top: 8px;
  }

  ol,
  ul {
    margin-top: 24px;
    padding-left: 32px;

    li + li {
      margin-top: 24px;
    }

    li {
      strong {
        color: ${(p) => p.theme.colors.textWhite};
      }

      p {
        color: #888;
      }
    }
  }

  p + ol,
  p + ul,
  ol + h2,
  ul + h2,
  ul + p {
    margin-top: 24px;
  }

  p + p {
    margin-top: 8px;
  }
`;

const Tabs = styled(FlexBox)`
  display: flex;
  margin-top: 64px;

  button {
    position: relative;
  }

  button + button {
    margin-left: 48px;
  }

  button:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 7px;
    right: -46%;
    width: 2px;
    height: 12px;
    margin: 0 24px;
    background-color: ${(p) => hexToRgba(p.theme.colors.textWhite, 0.4)};
  }
`;
const Tab = styled.button<{ $isActive: boolean }>`
  font-size: ${(p) => p.theme.size.md};
  ${(p) => p.theme.font.bold};
  color: ${(p) =>
    p.$isActive
      ? p.theme.colors.point
      : hexToRgba(p.theme.colors.textWhite, 0.4)};

  ${(p) =>
    p.$isActive &&
    `
    opacity: 1;
  `}
`;

export default function Terms() {
  const [index, setIndex] = useState(0);

  const handleTabClick = useCallback(
    (index: number) => () => {
      setIndex(index);
    },
    []
  );

  return (
    <Container
      as={motion.div}
      variants={dashboardContainerVariant}
      initial="hidden"
      animate="show"
      exit="exit"
      $direction="column"
    >
      <Tabs>
        <Tab type="button" $isActive={index === 0} onClick={handleTabClick(0)}>
          Privacy Policy
        </Tab>
        <Tab type="button" $isActive={index === 1} onClick={handleTabClick(1)}>
          Terms of Use
        </Tab>
      </Tabs>
      <TermsWrapper>{index === 0 ? POLICY : TERMS}</TermsWrapper>
    </Container>
  );
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common', 'dialog'])),
  },
});
